import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { getGraphMailSendToken } from '../../graphTokenHelper';
import {
  ArrowLeft, AlertCircle, Upload, Download, FileText,
  Plus, Send, Eye, CheckCircle, CheckCircle2, XCircle,
  Ban, Trash2, RotateCcw, Pencil, Circle,
} from 'lucide-react';
import { API_BASE } from '../helpers';
import DMCreateEnvelopeModal from './DMCreateEnvelopeModal';

const EVENT_ICONS = {
  created: Plus,
  sent: Send,
  viewed: Eye,
  signed: CheckCircle,
  completed: CheckCircle2,
  declined: XCircle,
  canceled: Ban,
  deleted: Trash2,
  restored: RotateCcw,
  edited: Pencil,
  downloaded: Download,
  error: AlertCircle,
};

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function cap(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function formatBytes(bytes) {
  if (bytes == null) return '';
  const n = Number(bytes);
  if (isNaN(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function DMEnvelopeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { instance: msalInstance, accounts: msalAccounts } = useMsal();

  const [detail, setDetail] = useState(null);
  const [usersById, setUsersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [editing, setEditing] = useState(false);

  const fileInputRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await axios.get(`${API_BASE}/dm/envelopes/${id}`);
      setDetail(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError('Could not load this envelope. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // Resolve user ids to names for "Created by" and timeline actors. Uses the
  // existing CRM users-list endpoint (best-effort — names degrade gracefully).
  useEffect(() => {
    axios.get(`${API_BASE}/options/users/list`)
      .then(res => {
        const map = {};
        (res.data || []).forEach(u => {
          map[u.UserId] = `${u.FirstName || ''} ${u.LastName || ''}`.trim();
        });
        setUsersById(map);
      })
      .catch(() => setUsersById({}));
  }, []);

  const envelope = detail?.envelope;
  const recipients = detail?.recipients || [];
  const documents = detail?.documents || [];
  const events = detail?.events || [];
  // `downloaded` events stay in the DB audit trail (the backend keeps
  // logging them) but are hidden from the user-facing activity timeline.
  const timelineEvents = events.filter(ev => ev.eventType !== 'downloaded');
  const originalDoc = documents.find(d => d.type === 'original');
  const signedDoc = documents.find(d => d.type === 'signed');
  const signer = recipients.find(r => r.role === 'signer');
  const ccs = recipients.filter(r => r.role === 'cc');

  const downloadDocument = async (doc) => {
    try {
      const res = await axios.get(
        `${API_BASE}/dm/envelopes/${id}/documents/${doc.documentId}/download`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setActionError('Could not download the document. Please try again.');
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf) {
      setActionError('Only PDF files can be attached.');
      return;
    }
    setActionError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_BASE}/dm/envelopes/${id}/documents`, fd);
      await fetchDetail();
    } catch (err) {
      // A 500 here is expected locally if Azure Blob Storage is not
      // configured; show a friendly message rather than crashing.
      setActionError('Could not upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  // Internal-provider envelopes need a Microsoft Graph Mail.Send token so the
  // backend can send the signing-link email on behalf of the staff user. For
  // Dropbox-Sign envelopes the backend ignores the header, and we deliberately
  // do NOT acquire the token (which would trigger an unrelated consent popup).
  // Returns an axios config object — empty for Dropbox, headers-bearing for
  // internal. Throws (with a friendly message) if the user declines consent.
  async function withGraphSendHeaders() {
    if (envelope?.provider !== 'internal') return {};
    const token = await getGraphMailSendToken(msalInstance, msalAccounts[0]);
    if (!token) {
      throw new Error('Microsoft 365 sign-in is required to send signing emails. Please allow the consent popup and try again.');
    }
    return { headers: { 'X-Graph-Token': token } };
  }

  const runAction = async (fn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'The action could not be completed. Please try again.');
      setBusy(false);
    }
  };

  // Spell out the provider in the Send confirmation so a Certified (billed,
  // legally-binding Dropbox Sign) send is never one unlabelled click away —
  // this is the last checkpoint before money/legal weight is committed.
  const sendConfirmMsg = envelope?.provider === 'dropbox-sign'
    ? 'This will be sent via Dropbox Sign as a CERTIFIED signature — a billed, legally-binding e-signature with a third-party audit trail.\n\nSend for Certified signature?'
    : 'This will be sent as a BASIC signature — a free, in-app signing link.\n\nSend for Basic signature?';

  const handleSend = () =>
    runAction(async () => {
      const config = await withGraphSendHeaders();
      await axios.post(`${API_BASE}/dm/envelopes/${id}/send`, null, config);
      await fetchDetail();
      setBusy(false);
    }, sendConfirmMsg);

  const handleCancel = () =>
    runAction(async () => {
      await axios.post(`${API_BASE}/dm/envelopes/${id}/cancel`);
      await fetchDetail();
      setBusy(false);
    }, 'Are you sure you want to cancel? This cannot be undone.');

  const handleDelete = () =>
    runAction(async () => {
      await axios.delete(`${API_BASE}/dm/envelopes/${id}`);
      navigate('/dm/envelopes');
    }, 'Move to trash?');

  const handleRestore = () =>
    runAction(async () => {
      await axios.post(`${API_BASE}/dm/envelopes/${id}/restore`);
      await fetchDetail();
      setBusy(false);
    });

  const handleResend = () =>
    runAction(async () => {
      const config = await withGraphSendHeaders();
      await axios.post(`${API_BASE}/dm/envelopes/${id}/resend`, null, config);
      await fetchDetail();
      setBusy(false);
    }, 'Send a new signing link to the recipient(s)? This will replace the previous link.');

  if (loading) {
    return (
      <div className="dm-center">
        <div className="dm-spinner" />
        <p className="crm-loading__text">Loading envelope...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="dm-center">
        <AlertCircle size={48} style={{ opacity: 0.4, marginBottom: '1rem' }} />
        <h2 style={{ margin: '0 0 0.5rem' }}>Envelope not found</h2>
        <Link className="dm-back-link" to="/dm/envelopes">
          <ArrowLeft size={16} /> Back to envelopes
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dm-center">
        <div className="crm-error" style={{ marginBottom: '1rem' }}>
          <AlertCircle size={20} className="crm-error__icon" />
          {error}
        </div>
        <button className="crm-btn crm-btn--secondary" onClick={fetchDetail}>Retry</button>
      </div>
    );
  }

  const status = envelope.status;
  const isDeleted = !!envelope.isDeleted;
  const createdByName = usersById[envelope.createdByUserId];

  const actorLabel = (ev) => {
    if (ev.actorType === 'user') return usersById[ev.actorId] || 'A Cagan user';
    if (ev.actorType === 'signer') return ev.actorEmail || 'Signer';
    return 'System';
  };

  return (
    <div>
      <Link className="dm-back-link" to="/dm/envelopes">
        <ArrowLeft size={16} /> Back to envelopes
      </Link>

      <div className="dm-summary">
        <div className="dm-summary__title-row">
          <h1 className="dm-summary__title">{envelope.title}</h1>
          <span className={`dm-badge dm-badge--${status}`}>{cap(status)}</span>
          {envelope.provider === 'internal' && (
            <span className="dm-badge dm-badge--provider-internal">Basic</span>
          )}
          {envelope.provider === 'dropbox-sign' && (
            <span className="dm-badge dm-badge--provider-certified">Certified</span>
          )}
          {isDeleted && <span className="dm-badge dm-badge--canceled">In Trash</span>}
        </div>
        <div className="dm-summary__subtitle">
          {envelope.documentCategory || 'Uncategorized'}
          {createdByName ? ` · Created by ${createdByName}` : ''}
          {` · Created ${formatDate(envelope.createdDate)}`}
        </div>
      </div>

      {actionError && (
        <div className="crm-error" style={{ marginBottom: '1.5rem' }}>
          <AlertCircle size={20} className="crm-error__icon" />
          {actionError}
        </div>
      )}

      <div className="dm-detail__grid">
        {/* LEFT COLUMN */}
        <div>
          {/* Recipients */}
          <div className="dm-section">
            <h2 className="dm-section__title">Recipients</h2>
            {signer ? (
              <div className="dm-recipient">
                <div>
                  <div className="dm-recipient__name">{signer.name}</div>
                  <div className="dm-recipient__email">{signer.email}</div>
                  <div className="dm-recipient__role">Signer</div>
                </div>
                <span className={`dm-badge dm-badge--${signer.status}`}>{cap(signer.status)}</span>
              </div>
            ) : (
              <p className="dm-table__sub">No signer assigned.</p>
            )}
            {ccs.map(cc => (
              <div className="dm-recipient" key={cc.recipientId}>
                <div>
                  <div className="dm-recipient__name">{cc.name}</div>
                  <div className="dm-recipient__email">{cc.email}</div>
                  <div className="dm-recipient__role">CC</div>
                </div>
                <span className={`dm-badge dm-badge--${cc.status}`}>{cap(cc.status)}</span>
              </div>
            ))}
          </div>

          {/* Documents */}
          <div className="dm-section">
            <h2 className="dm-section__title">Documents</h2>

            {signedDoc ? (
              <>
                <div className="dm-file">
                  <FileText size={20} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <div className="dm-file__meta">
                    <div className="dm-file__name">{signedDoc.fileName}</div>
                    <div className="dm-file__size">{formatBytes(signedDoc.fileSize)}</div>
                  </div>
                  <button
                    className="crm-btn dm-btn--gold"
                    onClick={() => downloadDocument(signedDoc)}
                  >
                    <Download size={16} /> Download Signed PDF
                  </button>
                </div>
                {originalDoc && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <button
                      className="dm-btn--delete-subtle"
                      style={{ color: 'var(--text-muted)', paddingLeft: 0 }}
                      onClick={() => downloadDocument(originalDoc)}
                    >
                      Download original ({originalDoc.fileName})
                    </button>
                  </div>
                )}
              </>
            ) : originalDoc ? (
              <div className="dm-file">
                <FileText size={20} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <div className="dm-file__meta">
                  <div className="dm-file__name">{originalDoc.fileName}</div>
                  <div className="dm-file__size">{formatBytes(originalDoc.fileSize)}</div>
                </div>
                <button
                  className="crm-btn crm-btn--secondary"
                  onClick={() => downloadDocument(originalDoc)}
                >
                  <Download size={16} /> Download
                </button>
              </div>
            ) : status === 'draft' ? (
              <>
                <div
                  className={`crm-dropzone ${dragActive ? 'crm-dropzone--active' : ''}`}
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={onDrop}
                >
                  {uploading ? (
                    <>
                      <div className="dm-spinner" style={{ width: 28, height: 28 }} />
                      <p className="dm-table__sub">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <Upload size={28} style={{ color: 'var(--text-muted)', marginBottom: '0.5rem' }} />
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem' }}>
                        Drag &amp; drop a PDF here, or click to choose
                      </p>
                      <p className="dm-table__sub" style={{ marginTop: '0.25rem' }}>
                        Only PDF files are accepted.
                      </p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    handleFile(e.target.files && e.target.files[0]);
                    e.target.value = '';
                  }}
                />
              </>
            ) : (
              <p className="dm-table__sub">No document attached.</p>
            )}
          </div>

          {/* Job Context */}
          {(envelope.propertyAddress || envelope.unitIdentifier || envelope.jobReference ||
            envelope.jobDate || envelope.internalNotes) && (
            <div className="dm-section">
              <h2 className="dm-section__title">Job Context</h2>
              <div className="dm-kv">
                {envelope.propertyAddress && (
                  <>
                    <div className="dm-kv__label">Property address</div>
                    <div className="dm-kv__value">{envelope.propertyAddress}</div>
                  </>
                )}
                {envelope.unitIdentifier && (
                  <>
                    <div className="dm-kv__label">Unit</div>
                    <div className="dm-kv__value">{envelope.unitIdentifier}</div>
                  </>
                )}
                {envelope.jobReference && (
                  <>
                    <div className="dm-kv__label">Job reference</div>
                    <div className="dm-kv__value">{envelope.jobReference}</div>
                  </>
                )}
                {envelope.jobDate && (
                  <>
                    <div className="dm-kv__label">Job date</div>
                    <div className="dm-kv__value">{formatDate(envelope.jobDate)}</div>
                  </>
                )}
                {envelope.internalNotes && (
                  <>
                    <div className="dm-kv__label">Internal notes</div>
                    <div className="dm-kv__value" style={{ whiteSpace: 'pre-wrap' }}>
                      {envelope.internalNotes}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div>
          <div className="dm-actions-panel">
            <h2 className="dm-section__title">Actions</h2>
            <div className="dm-actions-panel__stack">
              {isDeleted ? (
                <button className="crm-btn dm-btn--gold" disabled={busy} onClick={handleRestore}>
                  <RotateCcw size={16} /> Restore
                </button>
              ) : status === 'draft' ? (
                <>
                  <button
                    className="crm-btn dm-btn--gold"
                    disabled={busy || !originalDoc}
                    onClick={handleSend}
                    title={!originalDoc ? 'Attach a document first' : undefined}
                  >
                    <Send size={16} /> Send for Signature
                  </button>
                  <button
                    className="crm-btn crm-btn--secondary"
                    disabled={busy}
                    onClick={() => setEditing(true)}
                  >
                    <Pencil size={16} /> Edit
                  </button>
                  <button className="dm-btn--delete-subtle" disabled={busy} onClick={handleDelete}>
                    <Trash2 size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                    Delete
                  </button>
                </>
              ) : (status === 'sent' || status === 'viewed') ? (
                <>
                  {envelope.provider === 'internal' && (
                    <button
                      className="crm-btn crm-btn--secondary"
                      disabled={busy}
                      onClick={handleResend}
                      title="Send a new signing link with a fresh 30-day expiration"
                    >
                      <Send size={16} /> Resend signing email
                    </button>
                  )}
                  <button
                    className="crm-btn dm-btn--cancel"
                    disabled={busy}
                    onClick={handleCancel}
                  >
                    <Ban size={16} /> Cancel Envelope
                  </button>
                  <button className="dm-btn--delete-subtle" disabled={busy} onClick={handleDelete}>
                    <Trash2 size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                    Delete
                  </button>
                </>
              ) : (
                <button className="dm-btn--delete-subtle" disabled={busy} onClick={handleDelete}>
                  <Trash2 size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                  Delete
                </button>
              )}
            </div>
          </div>

          <div className="dm-actions-panel" style={{ position: 'static' }}>
            <h2 className="dm-section__title">Activity</h2>
            {timelineEvents.length === 0 ? (
              <p className="dm-table__sub">No activity yet.</p>
            ) : (
              <div className="crm-activity-timeline">
                {timelineEvents.map(ev => {
                  const Icon = EVENT_ICONS[ev.eventType] || Circle;
                  return (
                    <div className="crm-activity-timeline__item" key={ev.eventId}>
                      <div className="crm-activity-timeline__marker" />
                      <div className="crm-activity-timeline__content">
                        <div className="crm-activity-timeline__header">
                          <Icon size={15} style={{ color: 'var(--text-muted)' }} />
                          <span className="crm-activity-timeline__date">
                            {formatDateTime(ev.eventDate)}
                          </span>
                        </div>
                        <p className="crm-activity-timeline__description">{ev.description}</p>
                        <div className="crm-activity-timeline__footer">
                          <span className="crm-activity-timeline__author">{actorLabel(ev)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <DMCreateEnvelopeModal
          mode="edit"
          envelope={detail}
          onClose={() => setEditing(false)}
          onSuccess={() => { setEditing(false); fetchDetail(); }}
        />
      )}
    </div>
  );
}

export default DMEnvelopeDetail;
